import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useBrandKits } from '@/hooks/useBrandKits';
import { readWorkspaceFromUrl, withoutWorkspaceParam } from '@/lib/workspace/workspaceUrl';

export interface WorkspaceUrlSyncState {
  /** The workspace id requested by `?w=`, if any. */
  requestedId: string | null;
  /**
   * True when `?w=` names a workspace this account does not have (or one that
   * has been archived). The caller MUST surface an explicit picker for this —
   * see the note on silent fallbacks below.
   */
  isUnresolvable: boolean;
}

/**
 * Makes `?w=<brandKitId>` addressable.
 *
 * PRECEDENCE, and why it is this way round: the URL wins on load. A link
 * someone was sent, a notification, a bookmark — all of them name a workspace
 * explicitly, and the stored pointer is only ever a default for when nothing
 * else said. This follows Clerk's `organizationSyncOptions`, which is the
 * closest well-documented precedent for URL-addressed tenancy.
 *
 * 🔴 AN UNRESOLVABLE `?w=` MUST NOT SILENTLY FALL BACK to the stored pointer.
 * It looks harmless and it is the origin of the worst class of bug here: the
 * URL says one workspace, the sidebar says another, and the user acts on
 * whichever they happened to read. Failing to an explicit picker is the only
 * option that cannot mislead. That is why this hook reports `isUnresolvable`
 * rather than quietly doing nothing.
 *
 * Sync is deliberately ONE-WAY (URL → state). Writing `?w=` back on every
 * switch would fight the router, dirty every shared link with a workspace the
 * recipient may not have, and risk a navigation loop. Surfaces that want a
 * shareable workspace-pinned link should build it explicitly.
 */
export function useWorkspaceUrlSync(): WorkspaceUrlSyncState {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    selectableKits: kits,
    activeKit,
    setActiveKit,
    isFetched,
    isActiveKitPending,
  } = useBrandKits();

  const requestedId = useMemo(() => readWorkspaceFromUrl(location.search), [location.search]);

  // Only attempt activation once per requested id. Without this, a switch that
  // the server rejects would be retried on every render.
  const attempted = useRef<string | null>(null);

  const isKnown = useMemo(
    () => Boolean(requestedId) && kits.some((k) => k.id === requestedId),
    [requestedId, kits],
  );

  useEffect(() => {
    if (!requestedId || !isFetched || !isKnown) return;
    if (activeKit?.id === requestedId) return;
    if (attempted.current === requestedId) return;

    attempted.current = requestedId;
    setActiveKit(requestedId);
  }, [requestedId, isFetched, isKnown, activeKit?.id, setActiveKit]);

  const honoured = useRef<string | null>(null);

  useEffect(() => {
    if (!requestedId) {
      honoured.current = null;
      return;
    }
    if (!activeKit?.id) return;
    if (activeKit.id === requestedId) {
      honoured.current = requestedId;
      return;
    }
    if (honoured.current !== requestedId) return;

    honoured.current = null;
    attempted.current = null;
    navigate(
      {
        pathname: location.pathname,
        search: withoutWorkspaceParam(location.search),
        hash: location.hash,
      },
      { replace: true },
    );
  }, [requestedId, activeKit?.id, location.pathname, location.search, location.hash, navigate]);

  return {
    requestedId,
    // Only meaningful once the list has actually loaded AND the account owner
    // is resolved — `kits` is the owner-filtered selectable list, empty until
    // then, so before that "not found" just means "not known yet", and
    // reporting it would flash a picker on every deep link.
    isUnresolvable: Boolean(requestedId) && isFetched && !isActiveKitPending && !isKnown,
  };
}
